/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import Graphic from '@arcgis/core/Graphic';
import {GlobalSearchResult} from '@ion/core/src/services/GlobalSearch';
import {
  Account,
  FindNearbyType,
  GeoFenceAttributes,
  GeoFenceStatus,
  User
} from '@ion/core/src/types/entities';
import {LayerChooserProps} from '@ion/dls/src/maps/layers/LayerChooser';
import {LayerSettingsProps} from '@ion/dls/src/maps/layers/LayerSettings';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';

// Geofence Layer
// Defines the unique identifier for the Geofence Layer
export const GEOFENCE_LAYER_ID = 'geofence';

export type LandmarkGeofenceLayersProps = LayerSettingsProps & {
  user?: User,
  opacity?: number,
  layers?: string[],
  borderOnly?: boolean,
  view: __esri.MapView,
  isTrackingV2?: boolean,
  globalGroupId?: number,
  savedGeofence?: object,
  canAddGeofence?: boolean,
  savingGeofence?: boolean,
  onFindNearyBy?: Function,
  onLayersChange?: Function,
  onOpacityChange: Function,
  onToggleExpand?: Function,
  accounts?: Array<Account>,
  findLabel?: FindNearbyType,
  isGeofenceAccess?: boolean,
  setGeofenceState?: Function,
  geofenceLabelState?: boolean,
  expandedLayer?: string | null,
  onBorderOnlyChange?: Function,
  geofenceStatus?: GeoFenceStatus,
  geofences?: GeoFenceAttributes[],
  onResetGeofenceStatus?: Function,
  onGeofenceLabelChange?: Function,
  setEditGeofenceGraphic?: Function,
  onFindButtonStateChange?: Function,
  editGeofenceGraphic?: __esri.Graphic,
  selectedGeoFences?: Array<any> | null,
  layerChooserProps?: LayerChooserProps,
  geofenceUpdates?: GeoFenceAttributes[],
  searchResult?: GlobalSearchResult | null,
  setFindNearbyPopupForGeofence?: Function,
  onFindNearbyPopupStatusChange?: Function,
  onFindNearbyBreadcrumbBackClick?: Function
};

export type GroupDefinitionType = {
  groupsDefinition: string,
  operator: string,
  value: string,
  isSelectedGeofenceOnly?: boolean
};

enum borderStyle {
  solid = 'solid',
  none = 'none'
};

// Defines the Fields for the Geofence Layer.
export const GEOFENCE_FIELDS = [
  {name: 'OBJECTID', type: 'oid', alias: 'OBJECTID'},
  {name: 'geofenceId', type: 'double', alias: 'geofenceId'},
  {name: 'landmarkAccountId', type: 'double', alias: 'landmarkAccountId'},
  {name: 'landmarkAccount', type: 'string', alias: 'landmarkAccount'},
  {name: 'description', type: 'string', alias: 'description'},
  {name: 'geofenceType', type: 'string', alias: 'geofenceType'},
  {name: 'landmarkCategory', type: 'string', alias: 'landmarkCategory'},
  {name: 'landmarkName', type: 'string', alias: 'landmarkName'},
  {name: 'landmarkRestricted', type: 'string', alias: 'landmarkRestricted'},
  {name: 'landmarkStatus', type: 'string', alias: 'landmarkStatus'},
  {name: 'landmarkType', type: 'string', alias: 'landmarkType'},
  {name: 'modifiedDate', type: 'double', alias: 'modifiedDate'},
  {name: 'geometry', type: 'geometry', alias: 'geometry'}
];

// Provides a utility to Return the Geofence Layer based on the params.
export const getGeofenceRenderer = (borderOnly: boolean): object => {
  return {
    type   : 'simple',
    symbol : {
      type    : 'simple-fill',
      style   : borderOnly ? borderStyle.none : borderStyle.solid,
      color   : dlsAlt('colors', 'geofenceBackground'),
      outline : {
        color : dlsColor('caBlue'),
        width : 2
      }
    }
  };
};

// Provided a utility to create a graphics and return array of graphics[Polygon].
export const createGeofenceGraphic = (features: any) => {
  if (features?.length) {
    const graphics = [];
    features?.forEach((feature) => {
      feature.geofenceId = feature?.OBJECTID;
      const {rings} = feature?.geometry || {};
      const polygon = {
        type             : 'polygon',
        spatialReference : {wkid: 102100},
        rings
      };
      const graphic = new Graphic({
        geometry   : polygon as any,
        attributes : feature
      });
      graphics.push(graphic);
    });
    return graphics;
  }
  return [];
};

/**
 * Provides a utility to return geofence definition based on given parameters
 *
 * const groupDefinition      = getGroupDefinition(accountIds);
 * layer.definitionExpression = getGeofenceDefinition(groupDefinition, '=', '123');
 *
 */
export const getGeofenceDefinition = ({
  value,
  operator,
  groupsDefinition,
  isSelectedGeofenceOnly = false
}: GroupDefinitionType): string => {
  if (isSelectedGeofenceOnly) {
    return `geofenceId ${operator} ${value}`;
  }
  return `(${groupsDefinition}) AND geofenceId ${operator} ${value}`;
};

// Provides a utility to return a Label class.
export const getLabelClass = () => {
  return {
    symbol : {
      type      : 'text',
      color     : dlsColor('grey1'),
      haloColor : 'white',
      haloSize  : 1,
      font      : {
        family : 'Roboto',
        size   : 12,
        weight : 'bold'
      }
    },
    labelPlacement      : 'below-center',
    labelExpressionInfo : {
      expression : '$feature.landmarkName'
    },
    minScale : 100000,
    maxScale : 0
  };
};

// Provides a utility to update the renderer for given layer based on the params.
export const updateGeofenceRenderer = (geofenceLayer: __esri.FeatureLayer, borderOnly: boolean) => {
  if (geofenceLayer) {
    (geofenceLayer as any).renderer = getGeofenceRenderer(borderOnly);
    geofenceLayer.refresh();
  }
};

// Provides a utility to set the visibility of the label for given layer.
export const setLabelVisibility = (geofenceLayer: __esri.FeatureLayer, visible: boolean) => {
  if (geofenceLayer) {
    (geofenceLayer as any).labelsVisible = visible;
    geofenceLayer.refresh();
  }
};

// Provides a utility to return a geometru based on the given params.
export const getGeometry = (geofenceLayer: __esri.FeatureLayer, objectId: number) => {
  const query               = geofenceLayer?.createQuery() as any;
  query.outSpatialReference = {wkid: 102100};
  query.where               = `geofenceId = ${objectId}`;
  return geofenceLayer?.queryFeatures(query).then((result) => {
    return result?.features[0]?.geometry;
  });
};

// Defines utility to validate when geofences is created or updated.
export const hasGeofenceChanged = (geofenceStatus: GeoFenceStatus) => {
  const isCreate = geofenceStatus?.isCreate;
  const isUpdate = geofenceStatus?.isUpdate;
  const success  = geofenceStatus?.success;
  return (isCreate || isUpdate) && success;
};
